// src/components/Projects/Calculator/index.tsx
import React, { useState, ChangeEvent } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import './style.css';

interface CalculatorData {
    byte: number;
    kb: number;
    mb: number;
    gb: number;
    tb: number;
    sec: number;
    min: number;
    hr: number;
    day: number;
}

const shortenNumber = (number: number): string => {
    return Intl.NumberFormat('en-US', {
        notation: "compact",
        maximumFractionDigits: 2
    }).format(number);
};

const SystemDesignCalculator: React.FC = () => {
    const [data, setData] = useState<CalculatorData>({
        byte: 0,
        kb: 0,
        mb: 0,
        gb: 0,
        tb: 0,
        sec: 0,
        min: 0,
        hr: 0,
        day: 0
    });

    const convertByte = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            byte: value,
            kb: Math.round(value / 1024 * 100000) / 100000,
            mb: Math.round(value / 1048576 * 100000) / 100000,
            gb: Math.round(value / 1073741824 * 100000) / 100000,
            tb: Math.round(value / 1099511627776 * 100000) / 100000
        });
    };

    const convertKB = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            kb: value,
            byte: Math.round(value * 1024 * 100000) / 100000,
            mb: Math.round(value / 1024 * 100000) / 100000,
            gb: Math.round(value / 1048576 * 100000) / 100000,
            tb: Math.round(value / 1073741824 * 100000) / 100000
        });
    };

    const convertMB = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            mb: value,
            byte: Math.round(value * 1048576 * 100000) / 100000,
            kb: Math.round(value * 1024 * 100000) / 100000,
            gb: Math.round(value / 1024 * 100000) / 100000,
            tb: Math.round(value / 1073741824 * 100000) / 100000
        });
    };

    const convertGB = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            gb: value,
            byte: Math.round(value * 1073741824 * 100000) / 100000,
            kb: Math.round(value * 1048576 * 100000) / 100000,
            mb: Math.round(value * 1024 * 100000) / 100000,
            tb: Math.round(value / 1024 * 100000) / 100000
        });
    };

    const convertTB = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            tb: value,
            byte: Math.round(value * 1099511627776 * 100000) / 100000,
            kb: Math.round(value * 1073741824 * 100000) / 100000,
            mb: Math.round(value * 1048576 * 100000) / 100000,
            gb: Math.round(value * 1024 * 100000) / 100000
        });
    };

    const convertSec = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            sec: value,
            min: Math.round(value * 60 * 100000) / 100000,
            hr: Math.round(value * 3600 * 100000) / 100000,
            day: Math.round(value * 86400 * 100000) / 100000
        });
    };

    const convertMin = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            min: value,
            sec: Math.round(value / 60 * 100000) / 100000,
            hr: Math.round(value * 60 * 100000) / 100000,
            day: Math.round(value * 1440 * 100000) / 100000
        });
    };

    const convertHr = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            hr: value,
            sec: Math.round(value / 3600 * 100000) / 100000,
            min: Math.round(value / 60 * 100000) / 100000,
            day: Math.round(value * 24 * 100000) / 100000
        });
    };

    const convertDay = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        setData({
            ...data,
            day: value,
            sec: Math.round(value / 86400 * 100000) / 100000,
            min: Math.round(value / 1440 * 100000) / 100000,
            hr: Math.round(value / 24 * 100000) / 100000
        });
    };

    const byteStr = data.byte.toLocaleString("en-US");
    const kbStr = data.kb.toLocaleString("en-US");
    const mbStr = data.mb.toLocaleString("en-US");
    const gbStr = data.gb.toLocaleString("en-US");
    const tbStr = data.tb.toLocaleString("en-US");

    const shortenedByte = shortenNumber(data.byte);
    const shortenedKB = shortenNumber(data.kb);
    const shortenedMB = shortenNumber(data.mb);
    const shortenedGB = shortenNumber(data.gb);
    const shortenedTB = shortenNumber(data.tb);

    const secStr = data.sec.toLocaleString("en-US");
    const minStr = data.min.toLocaleString("en-US");
    const hrStr = data.hr.toLocaleString("en-US");
    const dayStr = data.day.toLocaleString("en-US");

    const shortenedSec = shortenNumber(data.sec);
    const shortenedMin = shortenNumber(data.min);
    const shortenedHr = shortenNumber(data.hr);
    const shortenedDay = shortenNumber(data.day);

    return (
        <Container>
            <h2 className="calculator-title">System Design Calculator</h2>
            <h3>Storage Calculator</h3>
            <p className="mb-3">
                Add a value for "# of bytes" in any of the input fields to convert the value into the other units.
            </p>
            <p className="mb-3" style={{ fontStyle: 'italic' }}>
                Example: "1" GB = 1024 MB, 1048576 KB, 1073741824 Bytes, 0.001 TB
            </p>
            <Form className="mb-2 calculator-form">
                <Row className="calculator-row">
                    <Col xs="auto" className="calculator-label-col calculator-label-col--input">
                        <strong>Input</strong>
                    </Col>
                    <Form.Group as={Col} controlId="formGridByte" onChange={convertByte} className="calculator-form-group">
                        <Form.Label>Byte</Form.Label>
                        <Form.Control type="text" value={byteStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridKB" onChange={convertKB} className="calculator-form-group">
                        <Form.Label>KB</Form.Label>
                        <Form.Control type="text" value={kbStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridMB" onChange={convertMB} className="calculator-form-group">
                        <Form.Label>MB</Form.Label>
                        <Form.Control type="text" value={mbStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridGB" onChange={convertGB} className="calculator-form-group">
                        <Form.Label>GB</Form.Label>
                        <Form.Control type="text" value={gbStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridTB" onChange={convertTB} className="calculator-form-group">
                        <Form.Label>TB</Form.Label>
                        <Form.Control type="text" value={tbStr} />
                    </Form.Group>
                </Row>
                <Row>
                    <Col xs="auto" className="calculator-label-col calculator-label-col--output">
                        <strong>Rounded</strong>
                    </Col>
                    <Form.Group as={Col} controlId="formGridByteRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedByte} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridKBRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedKB} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridMBRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedMB} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridGBRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedGB} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridTBRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedTB} readOnly />
                    </Form.Group>
                </Row>
            </Form>

            <h3 className="mt-5">Load Calculator</h3>
            <p className="mb-3">
                Add a value for "# of requests" in any of the input fields to convert the value into the other units.
            </p>
            <p className="mb-3" style={{ fontStyle: 'italic' }}>
                Example: "1" req/sec = 60 req/min, 3600 req/hr, 86400 req/day
            </p>
            <Form className="mb-2 calculator-form">
                <Row className="calculator-row">
                    <Col xs="auto" className="calculator-label-col calculator-label-col--input">
                        <strong>Input</strong>
                    </Col>
                    <Form.Group as={Col} controlId="formGridSec" onChange={convertSec} className="calculator-form-group">
                        <Form.Label>Req/sec</Form.Label>
                        <Form.Control type="text" value={secStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridMin" onChange={convertMin} className="calculator-form-group">
                        <Form.Label>Req/min</Form.Label>
                        <Form.Control type="text" value={minStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridHr" onChange={convertHr} className="calculator-form-group">
                        <Form.Label>Req/hr</Form.Label>
                        <Form.Control type="text" value={hrStr} />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridDay" onChange={convertDay} className="calculator-form-group">
                        <Form.Label>Req/day</Form.Label>
                        <Form.Control type="text" value={dayStr} />
                    </Form.Group>
                </Row>
                <Row>
                    <Col xs="auto" className="calculator-label-col calculator-label-col--output">
                        <strong>Rounded</strong>
                    </Col>
                    <Form.Group as={Col} controlId="formGridSecRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedSec} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridMinRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedMin} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridHrRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedHr} readOnly />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formGridDayRounded" className="calculator-form-group">
                        <Form.Control type="text" placeholder={shortenedDay} readOnly />
                    </Form.Group>
                </Row>
            </Form>
        </Container>
    );
};

export default SystemDesignCalculator;

